import styled from 'styled-components'
import { useQuery } from '@apollo/client'
import { useSearchParams } from 'react-router-dom'
import SearchList, { SearchListWrapper } from '../components/SearchList'
import { TitleButton } from '../components/TitleButton'
import LabelOnly from '../components/LabelOnly'
import { findOneBuyTOUGQL } from '../gql/PurchasePlans.gql'
import { getPvRevenueContractType } from '../util/algorithm/pvRevenue'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px #00000029;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
`

const Body = styled.div`
  display: flex;
  padding: 24px;
  gap: 40px;
`
const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 50%;
`

const GraphFrame = styled.div`
  padding: 0px 0px 0px 10px;
`

const GraphWrapper = styled.div`
  display: flex;
  height: 120px;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--gray10, #fff);
`
const GraphBody = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`

const LabelTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TableWrapper = styled.div`
  padding: 0px 0px 0px 10px;
`

const Table = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  border: 1px solid transparent;
`

const TableRow = styled.div`
  display: flex;
`

interface TableGrayProps {
  white?: boolean
}

const TableCell = styled.div<TableGrayProps>`
  display: flex;
  width: 122.8px;
  height: 38px;
  padding: 8px 0px 8px 0px;
  border: 0.5px solid #e6e6e6;
  background: ${(props) => (props.white ? '' : '#f4f5f8')};

  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  letter-spacing: 0em;
  align-items: center;
  justify-content: center;
`

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const OptionListWrapper = styled.div`
  display: flex;
  padding-left: 10px;
  width: 100%;
  align-items: flex-start;
  gap: 16px;
`

const OptionDetailWrapper = styled.div`
  display: flex;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 4px;
  border: 1px solid var(--gray30, #e6e6e6);
  background: var(--gray10, #fff);
`

const OptionDetailBox = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  gap: 4px;
`
const Rectangle = styled.div`
  width: 4px;
  height: 14px;
  border-radius: 99px;
  background: var(--green100, #57c9a7);
`
const OptionTextBox = styled.div`
  padding-top: 1px;
  width: max-content;
  color: var(--bluegray50, #2f3c5e);
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
`

const getSmpOptLabel = (smpOpt?: string) => {
  switch (smpOpt) {
    case 'min':
      return '일 최소 SMP'
    case 'max':
      return '일 최대 SMP'
    case 'weightedAver':
      return '월 가중 평균 SMP'
    case 'realTime':
    default:
      return '실시간 SMP'
  }
}

export const PurchasePlansCheck = () => {
  const [searchParams] = useSearchParams()
  const planId =
    searchParams.get('id') ||
    searchParams.get('_id') ||
    searchParams.get('val') ||
    ''
  const { data } = useQuery(findOneBuyTOUGQL, {
    variables: { val: planId },
    skip: !planId,
  })
  const planData = data?.findOneBuyTOU
  const revenueContractType = getPvRevenueContractType(planData?.cust)
  const canSelectRevenueSmpOpt = revenueContractType === 'marketSmp'

  return (
    <>
      <SearchListWrapper>
        <SearchList />
      </SearchListWrapper>
      <ContentWrapper>
        <Header>
          <TitleButton title="홍길동 고객 - 000요금제"></TitleButton>
        </Header>
        <Body>
          <Left>
            <LabelOnly label="시간대 그래프" />
            <GraphFrame>
              <GraphWrapper>
                <GraphBody>그래프</GraphBody>
              </GraphWrapper>
            </GraphFrame>
            <LabelTable>
              <TableWrapper>
                <Table>
                  <TableRow>
                    <TableCell
                      style={{ borderTopLeftRadius: '4px' }}
                    ></TableCell>
                    <TableCell>봄</TableCell>
                    <TableCell>여름</TableCell>
                    <TableCell>가을</TableCell>
                    <TableCell style={{ borderTopRightRadius: '4px' }}>
                      겨울
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>경부하</TableCell>
                    <TableCell white>0</TableCell>
                    <TableCell white>0</TableCell>
                    <TableCell white>0</TableCell>
                    <TableCell white>0</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>중간부하</TableCell>
                    <TableCell white>0</TableCell>
                    <TableCell white>0</TableCell>
                    <TableCell white>0</TableCell>
                    <TableCell white>0</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>최대부하</TableCell>
                    <TableCell white>0</TableCell>
                    <TableCell white>0</TableCell>
                    <TableCell white>0</TableCell>
                    <TableCell white style={{ borderBottomRightRadius: '4px' }}>
                      0
                    </TableCell>
                  </TableRow>
                </Table>
              </TableWrapper>
            </LabelTable>
          </Left>
          <Right>
            <LabelOnly label="선택한 옵션 목록" />
            <OptionListWrapper>
              <OptionDetailWrapper>
                <OptionDetailBox>
                  <Rectangle />
                  <OptionTextBox>SMP 데이터 : </OptionTextBox>
                  <OptionTextBox>2023년 SMP 데이터</OptionTextBox>
                </OptionDetailBox>
                <OptionDetailBox>
                  <Rectangle />
                  <OptionTextBox>한전 요금제 : </OptionTextBox>
                  <OptionTextBox>23.01.01 개정</OptionTextBox>
                </OptionDetailBox>
                <OptionDetailBox>
                  <Rectangle />
                  <OptionTextBox>현물 REC : </OptionTextBox>
                  <OptionTextBox>2023년 REC</OptionTextBox>
                </OptionDetailBox>
                <OptionDetailBox>
                  <Rectangle />
                  <OptionTextBox>공휴일 정보 : </OptionTextBox>
                  <OptionTextBox>2023년 공휴일</OptionTextBox>
                </OptionDetailBox>
              </OptionDetailWrapper>
              {/* 구분선*/}
              <OptionDetailWrapper>
                {canSelectRevenueSmpOpt ? (
                  <OptionDetailBox>
                    <Rectangle />
                    <OptionTextBox>SMP 선택 : </OptionTextBox>
                    <OptionTextBox>
                      {getSmpOptLabel(planData?.smpOpt)}
                    </OptionTextBox>
                  </OptionDetailBox>
                ) : null}
                <OptionDetailBox>
                  <Rectangle />
                  <OptionTextBox>PV 구입단가 선택 : </OptionTextBox>
                  <OptionTextBox>직접입력</OptionTextBox>
                </OptionDetailBox>
                <OptionDetailBox>
                  <Rectangle />
                  <OptionTextBox>TOU 시간대 : </OptionTextBox>
                  <OptionTextBox>한전 TOU 시간대</OptionTextBox>
                </OptionDetailBox>
                <OptionDetailBox>
                  <Rectangle />
                  <OptionTextBox>가격비율 : </OptionTextBox>
                  <OptionTextBox>한전 TOU 요금제</OptionTextBox>
                </OptionDetailBox>
              </OptionDetailWrapper>
            </OptionListWrapper>
          </Right>
        </Body>
      </ContentWrapper>
    </>
  )
}
